import { useCallback, useMemo, useRef } from "react"
import Placeholder from "@tiptap/extension-placeholder"

import { cn, getClipboardFiles } from "@productlane/lib/src/browser"
import { typographyVariants } from "@productlane/ui"

import type { Editor } from "@tiptap/core"
import type { EditorView } from "@tiptap/pm/view"
import { ALLOWED_FILE_SIZE_IN_MB } from "@/constants"
import { api } from "@/lib/api"
import { toast } from "../toast"
import { DEFAULT_EDITOR_PROPS } from "./editor-config"
import { EmojiSuggestion } from "./extensions/emoji"
import slashCommands from "./extensions/slash-commands/slash-commands"
import { getIssueDescriptionSchemaExtensions } from "./issue-description-schema"
import { issueDescriptionSuggestion } from "./issue-description-suggestion"
import { insertMarkdownPaste } from "./markdown-paste"
import { useCustomEditor } from "./use-comment-editor"

interface Props {
	placeholder?: string
	onUpdate?: (html: string, isEmpty: boolean) => void
	editable?: boolean
	className?: string
}

export function useIssueDescriptionEditor({
	placeholder,
	onUpdate,
	editable = true,
	className,
}: Props) {
	const getSignedUrlMutation = api.file.getUrl.useMutation()
	const editorRef = useRef<Editor | null>(null)

	const uploadImage = useCallback(
		async (file: File, view: EditorView) => {
			try {
				if (file.size / 1024 / 1024 >= ALLOWED_FILE_SIZE_IN_MB) {
					throw new Error(
						`Media files need to be less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
					)
				}

				const { uploadUrl, fileUrl } = await getSignedUrlMutation.mutateAsync({
					fileName: file.name,
					fileType: file.type,
				})
				const res = await fetch(uploadUrl, {
					method: "PUT",
					body: file,
					headers: { "Content-Type": file.type },
				})
				if (!res.ok) {
					throw new Error("File upload failed")
				}

				const node = view.state.schema.nodes.image?.create({ src: fileUrl })
				if (!node) {
					throw new Error("Failed to create image node")
				}
				view.dispatch(view.state.tr.replaceSelectionWith(node))
			} catch (error) {
				toast.error({
					title:
						error instanceof Error ? error.message : "Failed to upload image",
				})
			}
		},
		[getSignedUrlMutation],
	)

	const extensions = useMemo(
		() => [
			...getIssueDescriptionSchemaExtensions(),
			EmojiSuggestion,
			slashCommands.configure({ suggestion: issueDescriptionSuggestion }),
			Placeholder.configure({
				placeholder: placeholder ?? "Add description...",
			}),
		],
		[placeholder],
	)

	const editor = useCustomEditor(
		{
			editable,
			onUpdate: ({ editor }) => onUpdate?.(editor.getHTML(), editor.isEmpty),
			editorProps: {
				...DEFAULT_EDITOR_PROPS,
				attributes: {
					...DEFAULT_EDITOR_PROPS.attributes,
					class:
						className ??
						cn(
							typographyVariants({ variant: "message", mode: "invert" }),
							"text-sm text-primary focus:outline-none focus:ring-0",
						),
				},
				handleDrop: (view, event, _slice, moved) => {
					if (!moved && event.dataTransfer?.files?.[0]) {
						const file = event.dataTransfer.files[0]
						if (file.type.startsWith("image")) {
							// eslint-disable-next-line @typescript-eslint/no-floating-promises
							uploadImage(file, view)
							return true
						}
					}
					return false
				},
				handlePaste: (view, event) => {
					const image = getClipboardFiles(event.clipboardData?.items).find(
						(file) => file.type.startsWith("image"),
					)
					if (image) {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						uploadImage(image, view)
						return true
					}

					return insertMarkdownPaste({
						view,
						event,
						editor: editorRef.current,
						extensions,
					})
				},
				// Keep pasted remote images, but drop inlined data: URIs - they blow up the markdown sent to Linear.
				transformPastedHTML(html) {
					return html.replace(/<img[^>]*src=["']data:[^>]*>/gi, "")
				},
			},
			extensions,
		},
		[],
	)

	editorRef.current = editor
	return editor
}
